/**
 * Routes. Code-based (no file-based generation step) so the router is testable and the
 * route table is reviewable in one screen.
 */
import {
  Link,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router'
import { AppShell } from '@/components/AppShell'
import { IngestScreen } from '@/features/ingest/IngestScreen'
import { ProjectScreen } from '@/features/project/ProjectScreen'
import { BlueprintScreen, type BlueprintTab } from '@/features/blueprint/BlueprintScreen'
import { PushScreen } from '@/features/push/PushScreen'

const TABS: BlueprintTab[] = ['changes', 'structure', 'dry-run', 'push', 'metrics']

function NotFound() {
  return (
    <div className="flex flex-col gap-2">
      <h1 className="text-[20px] font-semibold text-mist-100">Not found</h1>
      <p className="text-[13px] text-mist-400">That route does not exist in this console.</p>
      <Link to="/" className="text-[13px] text-forge-300 underline decoration-dotted">
        Back to the start
      </Link>
    </div>
  )
}

const rootRoute = createRootRoute({
  component: AppShell,
  notFoundComponent: NotFound,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IngestScreen,
})

const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId',
  component: function ProjectRoute() {
    const { projectId } = projectRoute.useParams()
    return <ProjectScreen projectId={projectId} />
  },
})

const blueprintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId/blueprints/$blueprintId',
  validateSearch: (search: Record<string, unknown>): { tab: BlueprintTab; against?: string } => ({
    tab: TABS.includes(search.tab as BlueprintTab) ? (search.tab as BlueprintTab) : 'changes',
    ...(typeof search.against === 'string' ? { against: search.against } : {}),
  }),
  component: function BlueprintRoute() {
    const { projectId, blueprintId } = blueprintRoute.useParams()
    const search = blueprintRoute.useSearch()
    const navigate = blueprintRoute.useNavigate()
    return (
      <BlueprintScreen
        projectId={projectId}
        blueprintId={blueprintId}
        tab={search.tab}
        against={search.against}
        onNavigateTab={(tab) =>
          void navigate({
            to: '/projects/$projectId/blueprints/$blueprintId',
            params: { projectId, blueprintId },
            search: (prev) => ({ ...prev, tab }),
            replace: true,
          })
        }
      />
    )
  },
})

const pushRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pushes/$pushId',
  component: function PushRoute() {
    const { pushId } = pushRoute.useParams()
    return <PushScreen pushId={pushId} />
  },
})

const routeTree = rootRoute.addChildren([indexRoute, projectRoute, blueprintRoute, pushRoute])

export { routeTree }

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: NotFound,
  scrollRestoration: true,
})

export function RootOutlet() {
  return <Outlet />
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
